import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Loader2, Plus, Package, ClipboardList, CheckCircle2, Trash2, PoundSterling, Boxes } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_C = {
  draft: 'bg-gray-100 text-gray-600', paid: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700', complete: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
};
const gbp = (n) => `£${(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const Fulfilment = () => {
  const [tab, setTab] = useState('orders');
  const [dash, setDash] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [catDlg, setCatDlg] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', description: '', item_type: 'one_off', price: '', deliverables: [{ name: '', desc: '' }] });

  const [ordDlg, setOrdDlg] = useState(false);
  const [ordForm, setOrdForm] = useState({ catalogue_item_id: '', customer_id: '', customer_name: '', partner_id: '', amount: '', commission_pct: '' });
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);

  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, o, p] = await Promise.all([
        axios.get(`${API}/fulfilment/dashboard`),
        axios.get(`${API}/fulfilment/catalogue?include_inactive=false`),
        axios.get(`${API}/fulfilment/orders`),
        axios.get(`${API}/commercial/partners`),
      ]);
      setDash(d.data); setCatalogue(c.data.items || []); setOrders(o.data.items || []); setPartners(p.data.items || []);
    } catch (e) { toast.error('Failed to load fulfilment data'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!ordDlg) return;
    const t = setTimeout(async () => {
      const { data } = await axios.get(`${API}/fulfilment/customers`, { params: { search: custSearch, limit: 20 } });
      setCustResults(data.items || []);
    }, 250);
    return () => clearTimeout(t);
  }, [custSearch, ordDlg]);

  const saveCatalogue = async () => {
    if (!catForm.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const dels = catForm.deliverables.filter((d) => d.name.trim());
      await axios.post(`${API}/fulfilment/catalogue`, {
        name: catForm.name, description: catForm.description, item_type: catForm.item_type,
        price: parseFloat(catForm.price) || 0, deliverables: dels,
      });
      toast.success('Catalogue item created');
      setCatDlg(false);
      setCatForm({ name: '', description: '', item_type: 'one_off', price: '', deliverables: [{ name: '', desc: '' }] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const deleteCatalogue = async (id) => {
    await axios.delete(`${API}/fulfilment/catalogue/${id}`);
    toast.success('Retired'); load();
  };

  const createOrder = async () => {
    if (!ordForm.catalogue_item_id) { toast.error('Choose a catalogue item'); return; }
    setBusy(true);
    try {
      const payload = {
        catalogue_item_id: ordForm.catalogue_item_id,
        customer_id: ordForm.customer_id || null,
        customer_name: ordForm.customer_name || null,
        partner_id: ordForm.partner_id || null,
        amount: ordForm.amount !== '' ? parseFloat(ordForm.amount) : null,
        commission_pct: ordForm.commission_pct !== '' ? parseFloat(ordForm.commission_pct) : null,
      };
      await axios.post(`${API}/fulfilment/orders`, payload);
      toast.success('Order created');
      setOrdDlg(false);
      setOrdForm({ catalogue_item_id: '', customer_id: '', customer_name: '', partner_id: '', amount: '', commission_pct: '' });
      setCustSearch('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const openOrder = async (id) => {
    const { data } = await axios.get(`${API}/fulfilment/orders/${id}`);
    setDetail(data);
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fulfilment/orders/${detail.id}/mark-paid`);
      setDetail(data); toast.success('Marked as paid'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const signOff = async (did) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fulfilment/orders/${detail.id}/deliverables/${did}/sign-off`);
      setDetail(data); toast.success('Deliverable signed off'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const cancelOrder = async () => {
    await axios.post(`${API}/fulfilment/orders/${detail.id}/cancel`);
    toast.success('Order cancelled'); setDetail(null); load();
  };

  const setDel = (i, key, val) => {
    const dels = [...catForm.deliverables]; dels[i] = { ...dels[i], [key]: val };
    setCatForm({ ...catForm, deliverables: dels });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="fulfilment-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Fulfilment</h1>
          <p className="text-gray-500 text-sm mt-1">Catalogue → orders → deliverables → sign-off. Commission auto-triggers to partners.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDlg(true)} data-testid="new-catalogue-btn"><Plus className="w-4 h-4 mr-1" /> Catalogue item</Button>
          <Button className="bg-[#00A79D] hover:bg-[#00857D]" onClick={() => setOrdDlg(true)} data-testid="new-order-btn"><Plus className="w-4 h-4 mr-1" /> New order</Button>
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="fulfilment-dashboard">
          {[
            { label: 'Open orders', value: dash.open_orders, icon: ClipboardList },
            { label: 'Total orders', value: dash.total_orders, icon: Boxes },
            { label: 'Pending deliverables', value: dash.pending_deliverables, icon: Package },
            { label: 'Paid revenue', value: gbp(dash.paid_revenue), icon: PoundSterling },
          ].map((s) => (
            <Card key={s.label}><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{s.label}</p>
                <s.icon className="w-4 h-4 text-[#00A79D]" />
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200">
        {[['orders', 'Orders'], ['catalogue', 'Catalogue']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k ? 'border-[#00A79D] text-[#00857D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
      ) : tab === 'orders' ? (
        <div className="space-y-2" data-testid="orders-list">
          {orders.length === 0 && <div className="border border-dashed border-gray-200 rounded-lg py-16 text-center text-gray-400">No orders yet. Create one from a catalogue item.</div>}
          {orders.map((o) => (
            <button key={o.id} onClick={() => openOrder(o.id)} data-testid={`order-row-${o.id}`}
              className="w-full text-left border border-gray-200 rounded-lg px-4 py-3 hover:border-[#00A79D]/40 transition-colors flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{o.ref} · {o.catalogue_name}</p>
                <p className="text-xs text-gray-500 truncate">{o.customer_name || 'No customer'}{o.partner_name ? ` · via ${o.partner_name}` : ''}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-gray-500">{o.deliverables_done}/{o.deliverables_total} signed</span>
                <span className="text-sm font-medium text-gray-800">{gbp(o.amount)}</span>
                <Badge className={STATUS_C[o.status]}>{o.status.replace('_', ' ')}</Badge>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalogue-list">
          {catalogue.length === 0 && <div className="col-span-full border border-dashed border-gray-200 rounded-lg py-16 text-center text-gray-400">No catalogue items. Add one to start selling.</div>}
          {catalogue.map((c) => (
            <Card key={c.id} data-testid={`catalogue-card-${c.id}`}><CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <p className="text-[11px] text-gray-400">{c.ref}</p>
                </div>
                <Badge variant="secondary" className="text-[10px] shrink-0">{c.item_type === 'subscription' ? 'Subscription' : 'One-off'}</Badge>
              </div>
              {c.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{c.description}</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="text-lg font-bold text-[#00857D]">{gbp(c.price)}</span>
                <span className="text-xs text-gray-500">{(c.deliverables || []).length} deliverables</span>
              </div>
              <Button variant="ghost" size="sm" className="mt-2 text-red-500 h-7 text-xs" onClick={() => deleteCatalogue(c.id)} data-testid={`retire-catalogue-${c.id}`}><Trash2 className="w-3 h-3 mr-1" /> Retire</Button>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* Catalogue dialog */}
      <Dialog open={catDlg} onOpenChange={setCatDlg}>
        <DialogContent data-testid="catalogue-dialog">
          <DialogHeader><DialogTitle>New catalogue item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name" /></div>
            <div><Label>Description</Label><Textarea value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} data-testid="cat-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={catForm.item_type} onValueChange={(v) => setCatForm({ ...catForm, item_type: v })}>
                  <SelectTrigger data-testid="cat-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="one_off">One-off</SelectItem><SelectItem value="subscription">Subscription</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Price (£)</Label><Input type="number" value={catForm.price} onChange={(e) => setCatForm({ ...catForm, price: e.target.value })} data-testid="cat-price" /></div>
            </div>
            <div>
              <Label>Default deliverables</Label>
              <div className="space-y-2 mt-1">
                {catForm.deliverables.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <Input placeholder="Deliverable name" value={d.name} onChange={(e) => setDel(i, 'name', e.target.value)} data-testid={`cat-del-${i}`} />
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setCatForm({ ...catForm, deliverables: [...catForm.deliverables, { name: '', desc: '' }] })}><Plus className="w-3 h-3 mr-1" /> Add deliverable</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDlg(false)}>Cancel</Button>
            <Button className="bg-[#00A79D] hover:bg-[#00857D]" onClick={saveCatalogue} disabled={busy} data-testid="save-catalogue-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order dialog */}
      <Dialog open={ordDlg} onOpenChange={setOrdDlg}>
        <DialogContent data-testid="order-dialog">
          <DialogHeader><DialogTitle>New order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Catalogue item</Label>
              <Select value={ordForm.catalogue_item_id} onValueChange={(v) => setOrdForm({ ...ordForm, catalogue_item_id: v })}>
                <SelectTrigger data-testid="ord-catalogue"><SelectValue placeholder="Choose an item" /></SelectTrigger>
                <SelectContent>{catalogue.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {gbp(c.price)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Customer</Label>
              <Input placeholder="Search companies…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="ord-customer-search" />
              {custResults.length > 0 && !ordForm.customer_id && (
                <div className="border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto">
                  {custResults.map((c) => (
                    <button key={c.id} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => { setOrdForm({ ...ordForm, customer_id: c.id, customer_name: c.name }); setCustSearch(c.name); }} data-testid={`ord-cust-${c.id}`}>{c.name}</button>
                  ))}
                </div>
              )}
              {ordForm.customer_id && <p className="text-xs text-[#00857D] mt-1">Selected: {ordForm.customer_name} <button className="underline ml-1" onClick={() => { setOrdForm({ ...ordForm, customer_id: '', customer_name: '' }); setCustSearch(''); }}>change</button></p>}
            </div>
            <div><Label>Partner (optional - who introduced this deal)</Label>
              <Select value={ordForm.partner_id || 'none'} onValueChange={(v) => setOrdForm({ ...ordForm, partner_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="ord-partner"><SelectValue placeholder="No partner" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No partner</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.default_split_pct}%)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (£, optional)</Label><Input type="number" placeholder="Catalogue price" value={ordForm.amount} onChange={(e) => setOrdForm({ ...ordForm, amount: e.target.value })} data-testid="ord-amount" /></div>
              <div><Label>Commission % (optional)</Label><Input type="number" placeholder="Partner default" value={ordForm.commission_pct} onChange={(e) => setOrdForm({ ...ordForm, commission_pct: e.target.value })} data-testid="ord-commission" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrdDlg(false)}>Cancel</Button>
            <Button className="bg-[#00A79D] hover:bg-[#00857D]" onClick={createOrder} disabled={busy} data-testid="save-order-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create order'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="order-detail-dialog">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{detail.ref} · {detail.catalogue_name} <Badge className={STATUS_C[detail.status]}>{detail.status.replace('_', ' ')}</Badge></DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-gray-600">
                <p><span className="text-gray-400">Customer:</span> {detail.customer_name || '-'}</p>
                <p><span className="text-gray-400">Amount:</span> {gbp(detail.amount)}</p>
                <p><span className="text-gray-400">Partner:</span> {detail.partner_name || '-'}</p>
                <p><span className="text-gray-400">Commission:</span> {detail.commission_pct != null ? `${detail.commission_pct}%` : '-'}</p>
              </div>
              {!detail.paid_at && detail.status !== 'cancelled' && (
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={markPaid} disabled={busy} data-testid="mark-paid-btn"><PoundSterling className="w-4 h-4 mr-1" /> Mark as paid</Button>
              )}
              {detail.paid_at && <p className="text-xs text-blue-600">✓ Paid · partner share committed</p>}
              <div>
                <p className="font-semibold text-gray-800 mb-2">Deliverables ({detail.deliverables_done}/{detail.deliverables_total} signed off)</p>
                <div className="space-y-2">
                  {(detail.deliverables || []).length === 0 && <p className="text-xs text-gray-400">No deliverables on this order.</p>}
                  {(detail.deliverables || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2" data-testid={`deliverable-${d.id}`}>
                      <div className="min-w-0">
                        <p className="text-gray-800 truncate">{d.name}</p>
                        {d.signed_off_by && <p className="text-[11px] text-gray-400">by {d.signed_off_by}</p>}
                      </div>
                      {d.status === 'signed_off' ? (
                        <span className="text-emerald-600 text-xs flex items-center gap-1 shrink-0"><CheckCircle2 className="w-4 h-4" /> Signed off</span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => signOff(d.id)} disabled={busy} data-testid={`signoff-${d.id}`}>Sign off</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              {detail.status !== 'cancelled' && detail.status !== 'complete' && <Button variant="ghost" className="text-red-500" onClick={cancelOrder} data-testid="cancel-order-btn">Cancel order</Button>}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fulfilment;
